import { sortBy } from '@poseidon/shared-utils';
import React from 'react';

import { isNotNull } from '@harmonizeai/poseidon-shared';
import { Button, ButtonGroup, Select } from '@poseidon/components/Input2';
import { Arrows } from '@poseidon/components/StrokeIcons';

export type OffsetPaginatorProps = {
  limit?: number;
  defaultLimit: number;
  // Upper bound on the page-size options (e.g. the backend's pagination `.max()`).
  // Options above it are filtered out so the selector can't produce a limit the
  // API rejects. Omit for no cap (default).
  maxLimit?: number;
  // Deepest offset the API accepts (its `.max()`): Next stops before it. Omit for no cap (default).
  maxOffset?: number;
  offset?: number;
  len?: number;
  // Everything matching, when the API reports it. Next then stops on the real last page; without it,
  // a full page (len >= limit) is taken to mean there's more, which offers one empty page past the end
  // when the total is an exact multiple of the limit.
  total?: number;
  onChange: (next: { limit?: number; offset?: number }) => void;
};
export const OffsetPaginator = ({
  limit,
  defaultLimit,
  maxLimit,
  maxOffset,
  offset,
  len,
  total,
  onChange,
}: OffsetPaginatorProps) => {
  // `||`, not `??`: a 0 limit (e.g. from a hand-edited URL) means "unset" here, same as the select shows.
  const limitDefined = limit || defaultLimit;
  const nextOffset = (offset ?? 0) + limitDefined;

  const hasMore = total != null ? nextOffset < total : len == null || len >= limitDefined;
  const goNext =
    hasMore && (maxOffset == null || nextOffset <= maxOffset) ? () => onChange({ limit, offset: nextOffset }) : undefined;
  const goPrev =
    offset != null && offset > 0 ? () => onChange({ limit, offset: Math.max(0, offset - limitDefined) }) : undefined;

  const options = React.useMemo(() => {
    return sortBy(
      Array.from(new Set([limit, defaultLimit, defaultLimit * 2, defaultLimit * 3].filter(isNotNull))).filter(
        (v) => maxLimit == null || v <= maxLimit,
      ),
    ).map((v) => ({ value: `${v}`, label: `${v}` }));
  }, [limit, defaultLimit, maxLimit]);

  return (
    <ButtonGroup size="sm">
      <Button icon={<Arrows.ArrowLeft />} onClick={goPrev} disabled={goPrev == null} aria-label="Previous page" />
      <Select
        className="min-w-16"
        placeholder="limit"
        options={options}
        value={`${limitDefined}`}
        onChange={(e) => onChange({ limit: Number.parseInt(e.target.value), offset: undefined })}
      />
      <Button icon={<Arrows.ArrowRight />} onClick={goNext} disabled={goNext == null} aria-label="Next page" />
    </ButtonGroup>
  );
};
